import { Component, OnInit, inject, output, DestroyRef } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, distinctUntilChanged, take } from 'rxjs';
import { PmClient, EnvironmentInfo } from '@snelstart-ventures/fiscaal/domain-logic';
import { SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { ButtonConfiguration, SharedUiButtonModule } from '@snelstart/ui-button';
import { ToastService } from '@snelstart/ui-toast';
import { createBalanceProviderFormGroup } from './administration-selection-dialog.form';
import { BalanceProviderAdministrationSelectionContext } from './administration-selection-dialog.model';
import { BalanceProviderService } from '../balance-provider.service';

@Component({
    selector: 'snelstart-venture-fiscaal-domain-logic-balance-provider-administration-selection-dialog',
    standalone: true,
    imports: [FormsModule, ReactiveFormsModule, SharedUiFormControlsModule, SharedUiButtonModule, AsyncPipe],
    providers: [BalanceProviderService],
    templateUrl: './administration-selection-dialog.component.html',
    styleUrl: './administration-selection-dialog.component.css',
})
export class BalanceProviderAdministrationSelectionDialogComponent implements OnInit {
    public readonly balanceProviderService = inject(BalanceProviderService);
    client = inject(PmClient);
    toastService = inject(ToastService);
    formBuilder = inject(FormBuilder);
    destroyRef = inject(DestroyRef);
    administrationSelected = output<BalanceProviderAdministrationSelectionContext>();

    formGroup = createBalanceProviderFormGroup(this.formBuilder);

    logoffBalanceProviderButtonConfiguration = ButtonConfiguration.create({
        canExecute: () => this.balanceProviderService.canLogoffBalanceProvider$,
        onClick: () => this.balanceProviderService.logoffBalanceProvider$,
        onError: (error) => console.error('Error logging off Balance Provider', error),
    });

    loginBalanceProviderAuthorizeButtonConfiguration = ButtonConfiguration.create({
        canExecute: () => this.balanceProviderService.canLoginBalanceProviderAuthorize$,
        onClick: () => this.balanceProviderService.loginBalanceProviderAuthorize$,
        onError: (error) => console.error('Error authenticating Balance Provider (Authorize)', error),
    });

    loginBalanceProviderAccessTokenButtonConfiguration = ButtonConfiguration.create({
        canExecute: () => this.balanceProviderService.canLoginBalanceProviderAccessToken$,
        onClick: () => this.balanceProviderService.loginBalanceProviderAccessToken$,
        onError: (error) => console.error('Error authenticating Balance Provider (Access Token)', error),
    });

    ngOnInit(): void {
        // Automatically select first balance provider if only one
        this.balanceProviderService.balanceProviders$.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (balanceProviders) => {
                if (balanceProviders.length === 1) {
                    if (this.formGroup.controls.balanceProviderId.value !== balanceProviders[0].id) {
                        this.formGroup.controls.balanceProviderId.setValue(balanceProviders[0].id);
                    }
                } else if (this.formGroup.controls.balanceProviderId.value != null) {
                    this.formGroup.controls.balanceProviderId.setValue(null);
                }
            },
        });

        // Select Balance Provider
        this.formGroup.controls.balanceProviderId.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (balanceProviderId) => {
                if (this.formGroup.controls.environmentId.value != null) {
                    this.formGroup.controls.environmentId.setValue(null);
                }
                if (this.formGroup.controls.administrationId.value != null) {
                    this.formGroup.controls.administrationId.setValue(null);
                }
                this.balanceProviderService.selectBalanceProvider(balanceProviderId);
            },
        });

        // Automatically select first environment if only one
        this.balanceProviderService.balanceProviderEnvironments$
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (environments) => {
                    if (environments.length === 1) {
                        if (this.formGroup.controls.environmentId.value !== environments[0].id) {
                            this.formGroup.controls.environmentId.setValue(environments[0].id);
                        }
                    } else if (this.formGroup.controls.environmentId.value != null) {
                        this.formGroup.controls.environmentId.setValue(null);
                    }
                },
            });

        // Select Environment
        this.formGroup.controls.environmentId.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (environmentId) => {
                if (this.formGroup.controls.administrationId.value != null) {
                    this.formGroup.controls.administrationId.setValue(null);
                }
                this.balanceProviderService.selectBalanceProviderEnvironment(environmentId);
            },
        });

        // Automatically select first administration if only one
        this.balanceProviderService.balanceProviderAdministrations$
            .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (administrations) => {
                    if (administrations.length === 1) {
                        if (this.formGroup.controls.administrationId.value !== administrations[0].id) {
                            this.formGroup.controls.administrationId.setValue(administrations[0].id);
                        }
                    } else if (this.formGroup.controls.administrationId.value != null) {
                        this.formGroup.controls.administrationId.setValue(null);
                    }
                },
            });

        // Select Administration
        this.formGroup.controls.administrationId.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (administrationId) => {
                this.balanceProviderService.selectBalanceProviderAdministration(administrationId);
            },
        });

        // Set Access Token
        this.formGroup.controls.accessToken.valueChanges.pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (accessToken) => {
                this.balanceProviderService.setIdentityProviderAccessToken(accessToken);
            },
        });
    }

    selectBalanceProviderAdministration(): void {
        combineLatest([
            this.balanceProviderService.selectedBalanceProvider$,
            this.balanceProviderService.identityProvider$,
            this.balanceProviderService.identityProviderStatus$,
            this.balanceProviderService.selectedBalanceProviderEnvironment$,
            this.balanceProviderService.selectedBalanceProviderAdministration$,
        ])
            .pipe(take(1))
            .subscribe(([balanceProvider, identityProvider, identityProviderStatus, environment, administration]) => {
                if (
                    balanceProvider &&
                    identityProvider &&
                    identityProviderStatus &&
                    administration &&
                    ((balanceProvider.requiresEnvironmentSelection && environment) || !environment)
                ) {
                    // Balance Provider working with access token authentication and requires administration selection requires an environment shared with the Identity Provider.
                    if (
                        balanceProvider.supportsMultipleEnvironments &&
                        balanceProvider.requiresAdministrationSelection &&
                        !environment &&
                        identityProvider.requiresEnvironment
                    ) {
                        environment = {
                            id: identityProviderStatus.environmentId,
                            name: administration.name,
                        } as EnvironmentInfo;
                    }

                    this.administrationSelected.emit({
                        balanceProvider: balanceProvider,
                        identityProvider: identityProvider,
                        identityProviderStatus: identityProviderStatus,
                        environment: environment,
                        administration: administration,
                    });
                }
            });
    }
}
